import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import ButtonV2 from "../../../components/ui/button/v2";
import { AddEntitiesModal } from "../../workbench/components/AddEntitiesModal";
import { StakeholderOption } from "../../creditAccounts/components/AccountNameSearchInput";
import {
  FullStakeholderData,
  getFullStakeholders,
} from "../../stakeholder/logic/stakeholderSlice";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";
import type { Stakeholder } from "../../../api/stakeholders";
import {
  EntityType,
  RelatedAs,
  StakeholderRelationship,
} from "../../../api/stakeholder_relationships";
import {
  AccountCategory,
  AccountType,
  CreateAccountData,
} from "../../../api/accounts";
import { getCaseAccounts } from "../logic/slice";
import { addAccounts } from "../logic/thunks";
import AccountRow, {
  AccountWithStakeholder,
  getAccountRowErrors,
} from "./AccountRow";

export interface AccountsModalProps {
  onClose: () => void;
  onBack?: () => void;
  prefillExisting?: boolean;
  title?: string;
}

const emptyAccount = (): AccountWithStakeholder => ({
  account_number: uuidv4(),
  ext_account_number: "",
  account_name: "",
  account_type: "",
  account_category: "",
});

const AccountsModal: React.FC<AccountsModalProps> = ({
  onClose,
  onBack,
  prefillExisting,
  title = "Add Accounts",
}) => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();
  const existingAccounts = useAppSelector(getCaseAccounts);
  const existingStakeholders = useAppSelector(getFullStakeholders);
  const [newStakeholders, setNewStakeholders] = useState<FullStakeholderData[]>([]);

  useEffect(() => {
    if (!caseId || existingStakeholders.length === 0) return;
    dispatch(
      fetchEnrichedStakeholder({
        caseId,
        stakeholderIds: existingStakeholders.map((s) => s.stakeholder.stakeholder_id),
      }),
    );
  }, [caseId, existingStakeholders, dispatch]);

  const existingRows: AccountWithStakeholder[] = existingAccounts.map((account) => ({
    ...account,
  }));

  const initialRows = (): AccountWithStakeholder[] =>
    prefillExisting ? [] : [emptyAccount()];

  const [rows, setRows] = useState<AccountWithStakeholder[]>(initialRows);
  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const rowCount = useRef(rows.length);

  const stakeholderOptions = useMemo<StakeholderOption[]>(() => {
    const existingOptions = existingStakeholders.map((stakeholder) => ({
      ...stakeholder,
      isNew: false,
    }));
    const newOptions = newStakeholders
      .filter((stakeholder) =>
        rows.some((a) => a.stakeholder_id === stakeholder.stakeholder.stakeholder_id),
      )
      .map((stakeholder) => {
        const relatedAccounts = rows.filter(
          (a) => a.stakeholder_id === stakeholder.stakeholder.stakeholder_id,
        );
        const relationships = relatedAccounts.map(
          (a): StakeholderRelationship => ({
            relationship_id: uuidv4(),
            stakeholder_id: stakeholder.stakeholder.stakeholder_id,
            entity_type: EntityType.ACCOUNT,
            entity_id: a.ext_account_number?.trim(),
            related_as: RelatedAs.PRIMARY_ACCOUNT_HOLDER,
          }),
        );
        return {
          ...stakeholder,
          relationships,
          isNew: true,
        };
      });

    return [...existingOptions, ...newOptions];
  }, [existingStakeholders, newStakeholders, rows]);

  useEffect(() => {
    const list = listRef.current;
    if (list && rows.length > rowCount.current) {
      list.scrollTop = list.scrollHeight;
    }
    rowCount.current = rows.length;
  }, [rows.length]);

  const allForUniqueness = [...existingRows, ...rows];

  const isComplete = rows.every(
    (row) => Object.keys(getAccountRowErrors(row, allForUniqueness)).length === 0,
  );

  const addRow = () => setRows((prev) => [...prev, emptyAccount()]);

  const updateRow = (
    accountNumber: string,
    patch: Partial<AccountWithStakeholder>,
  ) => {
    if (patch.stakeholder_id && !patch.account_name) {
      patch.account_name = stakeholderOptions.find(
        (option) => option.stakeholder.stakeholder_id === patch.stakeholder_id,
      )?.stakeholder.name;
    }
    setRows((prev) =>
      prev.map((row) =>
        row.account_number === accountNumber ? { ...row, ...patch } : row,
      ),
    );
  };

  const addStakeholder = (name: string, accountNumber: string) => {
    const stakeholder_id = uuidv4();
    const newStakeholder: FullStakeholderData = {
      stakeholder: { stakeholder_id, name },
      enriched_stakeholder: { stakeholder_id },
      relationships: [],
    };
    setNewStakeholders((prev) => [...prev, newStakeholder]);
    updateRow(accountNumber, { stakeholder_id, account_name: name });
  };

  const deleteRow = (accountNumber: string) =>
    setRows((prev) => prev.filter((row) => row.account_number !== accountNumber));

  const handleDiscard = () => {
    setRows(initialRows());
    onClose();
  };

  const handleSave = async () => {
    setShowErrors(true);
    if (!caseId || !isComplete || rows.length === 0) return;
    setIsSaving(true);

    const stakeholders: Stakeholder[] = newStakeholders
      .filter((s) =>
        rows.some((a) => a.stakeholder_id === s.stakeholder.stakeholder_id),
      )
      .map((s) => s.stakeholder);

    try {
      const accounts: CreateAccountData[] = rows.map((row) => ({
        account_number: row.account_number,
        ext_account_number: row.ext_account_number.trim(),
        account_name: row.account_name.trim(),
        account_type: row.account_type as AccountType,
        account_category: row.account_category as AccountCategory,
        primary_account_holder_stakeholder_id: row.stakeholder_id,
      }));
      await dispatch(
        addAccounts({ case_id: caseId, accounts, stakeholders }),
      ).unwrap();
      showSuccessToast("Accounts saved successfully.");
      onClose();
    } catch {
      showErrorToast("Failed to save accounts. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AddEntitiesModal
      title={title}
      onClose={onClose}
      onBack={onBack}
      onDiscard={handleDiscard}
      onSave={handleSave}
      saveEnabled={rows.length > 0 && !isSaving && isComplete}
      isSaving={isSaving}
      wide
    >
      <div ref={listRef} className="flex max-h-[50vh] flex-col overflow-y-auto px-3 py-1 gap-4">
        {prefillExisting &&
          existingRows.map((account, index) => (
            <AccountRow
              key={account.account_number}
              account={account}
              errors={{}}
              stakeholderOptions={stakeholderOptions}
              showLabels={index === 0}
              showErrors={false}
              disabled
              canDelete={false}
              onAccountChange={() => {}}
              onAddStakeholder={() => {}}
              onDelete={() => {}}
            />
          ))}

        {rows.map((account, index) => (
          <AccountRow
            key={account.account_number}
            account={account}
            errors={getAccountRowErrors(account, allForUniqueness)}
            stakeholderOptions={stakeholderOptions}
            showLabels={!prefillExisting && index === 0}
            showErrors={showErrors}
            disabled={isSaving}
            canDelete={rows.length > 1}
            onAccountChange={(patch) => updateRow(account.account_number, patch)}
            onAddStakeholder={(name) => addStakeholder(name, account.account_number)}
            onDelete={() => deleteRow(account.account_number)}
          />
        ))}
      </div>

      <ButtonV2
        variant="ghost"
        size="sm"
        disabled={!isComplete || isSaving}
        onClick={addRow}
        className="text-[#3551F3] bg-transparent self-start disabled:opacity-40"
        prefix={<Plus width="16" height="16" />}
      >
        Add Account
      </ButtonV2>
    </AddEntitiesModal>
  );
};

export default AccountsModal;